/** Author: @seetam-cultsport */
/**
 * Breeze Cart Offers Integration
 *
 * This module integrates Breeze/BlazeSDK to display eligible offers in the shopping cart.
 * It handles:
 * - Fetching cart data and transforming it for the Breeze API
 * - Making authenticated API calls to get available offers
 * - Rendering offer cards in the cart drawer with expandable details
 * - Updating offers when cart contents change
 */

// Initialize offers storage on window object
window.BlazeSDK = window.BlazeSDK || {};
window.breeze = window.breeze || {};

window.allOffers = {
  pdpOffers: [],
  cartOffers: [],
  inEligibleOffers: {
    cart: [],
    pdpOffers: [],
  },
  cartData: {
    cartTotal: 0,
  },
};

// Add these variables at the top of your file
let pdpOffersRequestInProgress = false;
let pdpOffersDebounceTimer = null;

// Utility Functions

window.breeze.generateShortUniqueId = (type = "cart") => {
  return (
    "id-" + type + "-" + Date.now() + "-" + Math.floor(Math.random() * 1000)
  );
};

/**
 * Retrieves and formats cart items for the Breeze API
 * Takes optional cart data parameter to avoid extra API call
 * Transforms Shopify cart format to Breeze format:
 * - Converts IDs to strings
 * - Normalizes product types
 * - Formats prices as decimal strings
 */
async function fetchCartItems(currentCartData = null) {
  try {
    // Use provided cart data or fetch from Shopify
    let cartdata = {
      items: currentCartData || [],
    };
    if (!cartdata.items || cartdata.items.length === 0) {
      let response = await fetch(window.Shopify.routes.root + "cart.js");
      cartdata = await response.json();
    }

    // Map cart items to Breeze format
    let cartItems = cartdata.items.map((item) => ({
      id: item.variant_id.toString(),
      type: item.product_type.replace(/\s+/g, "_"),
      quantity: item.quantity,
      price: item.line_price > 0 ? (item.price / 100).toString() : "0", // if line items line price is 0, set price to 0
    }));

    return cartItems;
  } catch (error) {
    console.error("Error fetching cart items:", error);
  }
  return [];
}

//dummy function to get offers for Customer for Weekender 
async function fetchCartOffersForLoggedInCustomer() {
  const customerId = getCustomerIdFromLocalStorage(); // Retrieve customer ID from local storage
  const cart = {
    items: [
      {
        id: "52972043272360",
        type: "Track_Pants",
        quantity: 2,
        price: "1179",
      },
    ],
  };

  const payload = {
    keyId: "56263",
    action: "listOffers",
    customerData: {
      id: customerId,
    },
    cart: JSON.stringify(cart),
  };

  const shopifyYCookie = getCookie("_shopify_y");
  try {
    const response = await fetch(
      "https://tesseract.cultstore.com/juspay/gen-offer-signature",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Device-ID": shopifyYCookie ?? "",
        },
        body: JSON.stringify(payload),
      },
    );
    const jsonResponse = await response.json();
    const signature = jsonResponse.data.signature;

    const requestId = window.breeze.generateShortUniqueId("cart");
    const processPayload = {
      requestId: requestId,
      service: "in.breeze.onecco",
      payload: {
        ...payload,
        signature: signature,
      },
    };

    // Ensure BlazeSDK is initialized before using it
    if (!window.BlazeSDK || typeof window.BlazeSDK.process !== "function") {
      console.error("BlazeSDK is not initialized or process is not a function");
      return;
    }

    // Call the process function
    window.BlazeSDK.process(processPayload);
  } catch (error) {
    console.error("Error fetching cart offers for logged-in customer:", error);
  }
}
// Remove this code after offer ends

/**
 * Main function to fetch and display offers
 *
 * Flow:
 * 1. Gets cart items and customer ID
 * 2. Generates signed API payload
 * 3. Makes authenticated request to Breeze
 * 4. Renders returned offers in cart
 */
const fetchAndRenderOffersInCart = async (cartData = null) => {
  const cartItems = await fetchCartItems(cartData);
  if (cartItems.length === 0) {
    return;
  }
  const customerId = getCustomerIdFromLocalStorage();

  // Build request payload with cart and customer data
  const payload = {
    keyId: "56263",
    action: "listOffers",
    customerData: {
      id: customerId,
    },
    cart: JSON.stringify({
      items: cartItems,
    }),
  };

  try {
    // Get security signature from backend
    const shopifyYCookie = getCookie("_shopify_y");
    const response = await fetch(
      "https://tesseract.cultstore.com/juspay/gen-offer-signature",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Device-ID": shopifyYCookie ?? "",
        },
        body: JSON.stringify(payload),
      },
    );
    const jsonResponse = await response.json();
    const signature = jsonResponse.data.signature;

    // Prepare final API request
    const requestId = window.breeze.generateShortUniqueId("cart");
    const processPayload = {
      requestId: requestId,
      service: "in.breeze.onecco",
      payload: {
        ...payload,
        signature: signature,
      },
    };

    // Verify SDK is ready
    if (!window.BlazeSDK || typeof window.BlazeSDK.process !== "function") {
      console.error("BlazeSDK is not initialized or process is not a function");
      return;
    }
    window.BlazeSDK.process(processPayload);

    // Check for applied offer and update cart subtotal
  } catch (error) {
    console.error("Error fetching and rendering offers in cart:", error);
  }
};

// HTML Generation

/**
 * Generates HTML for individual offer card
 * Includes:
 * - Best offer badge for first offer
 * - Offer code and description
 * - Expandable terms and conditions
 * - Show/hide buttons for T&C
 */
window.breeze.generateCartOfferItemHTML = function (
  offer,
  index,
  isPdp = false,
) {
  const isBestOffer = index === 0;
  const cartTotal = window.allOffers.cartData.cartTotal;
  const amountToUnlock = isPdp
    ? window.cultstore.productData.selectedVariant.price / 100
    : cartTotal;

  const finalOrderAmount = offer.orderBreakup.finalOrderAmount;
  const displayAmount =
    finalOrderAmount > 0
      ? `₹${Math.trunc(finalOrderAmount)}`
      : finalOrderAmount == 0
        ? "free"
        : "N/A";

  let appliedOffer = JSON.stringify(localStorage.getItem("appliedOffer"));
  let active = false;
  if (appliedOffer && appliedOffer.code === offer.offerCode) {
    active = true;
  }
  if (isPdp) {
    return `
      <discount-code-item>
            ${isBestOffer ? '<div class="cart-offer-callout-card-header">Best Offer - Apply at Checkout</div>' : ""}
            <div class="cart-offer-callout-card">
                <div class="cart-offer-callout-card-body">
                    <div class="cart-offer-callout-inner-card-body">
                        <div class="cart-offer-callout-card-body-header ${offer.status === "ELIGIBLE" ? "row-reverse" : "column-reverse"}">
                            <div><p>${offer.status === "ELIGIBLE" ? (offer.orderBreakup.finalOrderAmount === 0 ? "Get for <span>free</span> with coupon" : "Get for <span>₹" + Math.trunc(offer.orderBreakup.finalOrderAmount) + "</span> with coupon") : offer.offerDescription.description}</p></div>
                            <div class="offer-code">${offer.offerCode}</div>
                        </div>
                    </div>
                </div>
                
                <div class="unlock-offer hidden ${offer.offerRules.amount.minOrderAmount < amountToUnlock ? "hidden" : ""}">Add items worth ₹${Math.trunc(offer.offerRules.amount.minOrderAmount - amountToUnlock)} to unlock</div>

                <div class="cart-offer-callout-card-body-footer">
                    <span class="cart-offer-callout-card-know-more-btn">Know More</span>
                    <div class="cart-offer-callout-card-tc-container hidden">
                        <h4>Terms and Conditions</h4>
                        <ul class="cart-offer-callout-card-tc-pointer">
                            ${generatePDPOfferItemTnCHTML(offer.offerDescription.tnc || "N/A")}
                        </ul>
                    </div>
                    <span class="cart-offer-callout-card-show-less-btn hidden">Show less</span>
                </div>
            </div>
        </discount-code-item>
      `;
  }
  return `
        <discount-code-item>
            ${isBestOffer ? '<div class="cart-offer-callout-card-header">Best Offer</div>' : ""}
            <div class="cart-offer-callout-card">
                <div class="cart-offer-callout-card-body">
                    <div class="cart-offer-callout-inner-card-body">
                        <div class="cart-offer-callout-card-body-header">
                            <div data-offer-id="${offer.offerId}" data-offer-code="${offer.offerCode}" data-cart-total="${Math.trunc(offer.orderBreakup.finalOrderAmount)}" class="offer-code">${offer.offerCode}</div>
                            <div class="${offer.offerRules.amount.minOrderAmount > cartTotal ? "" : "hidden"}"><p>${offer.offerDescription.description}</p></div>
                            <div class="cart-offer-apply-container apply-btn ${offer.offerRules.amount.minOrderAmount > cartTotal ? "hidden" : ""}"><p style="margin: 0;">Get for <span data-discount-amount="${Math.trunc(offer.orderBreakup.discountAmount)}">${displayAmount}</span> with coupon</p><span class="apply-btn-text">${active ? "REMOVE" : "APPLY"}</span></div>
                        </div>
                    </div>
                </div>
                
                <div class="unlock-offer hidden ${offer.offerRules.amount.minOrderAmount < amountToUnlock ? "hidden" : ""}">Add items worth ₹${Math.trunc(offer.offerRules.amount.minOrderAmount - amountToUnlock)} to unlock</div>

                <div class="cart-offer-callout-card-body-footer">
                    <span class="cart-offer-callout-card-know-more-btn">Know More</span>
                    <div class="cart-offer-callout-card-tc-container hidden">
                        <h4>Terms and Conditions</h4>
                        <ul class="cart-offer-callout-card-tc-pointer">
                            ${generatePDPOfferItemTnCHTML(offer.offerDescription.tnc || "N/A")}
                        </ul>
                    </div>
                    <span class="cart-offer-callout-card-show-less-btn hidden">Show less</span>
                </div>
            </div>
        </discount-code-item>
      `;
};

/**
 * Generates complete offers section HTML
 * Features:
 * - Shows best offer first
 * - Collapsible section for additional offers
 * - Offer count indicator
 * - Animated expand/collapse button
 */
window.BlazeSDK.generateCartOfferHTML = function (offers, ieOffers = []) {
  let offerItemHTML = offers
    .sort((a, b) => b.orderBreakup.offerAmount - a.orderBreakup.offerAmount)
    .map((offer, index) =>
      window.breeze.generateCartOfferItemHTML(offer, index),
    );
  let ieOffersItemHTML = ieOffers
    .filter(
      (off) =>
        off.offerRules.amount.minOrderAmount >
        window.allOffers.cartData.cartTotal,
    )
    .sort((a, b) => {
      const cartTotal = window.allOffers.cartData.cartTotal;
      const diffA = a.offerRules.amount.minOrderAmount - cartTotal;
      const diffB = b.offerRules.amount.minOrderAmount - cartTotal;

      if (diffA === diffB) {
        return b.orderBreakup.offerAmount - a.orderBreakup.offerAmount;
      }

      return diffA - diffB;
    })
    .map((ieOffers, index) =>
      window.breeze.generateCartOfferItemHTML(ieOffers, index + 1),
    );
  return `
        <extra-offers-container class="cart-offer-callout-card-container">
            ${
              !offerItemHTML[0] && ieOffersItemHTML[0]
                ? `
            <div class="cart-offer-callout-card-show-more-container-ineligible">
              <div class="ineligible-title">
                  <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M4.52482 5.71433H11.4968L11.4976 4.88264C11.4976 2.95737 9.93688 1.39663 8.01161 1.39663L7.83763 1.4009C5.99319 1.49156 4.52561 3.01571 4.52561 4.88264L4.52482 5.71433ZM12.8945 5.75652V4.88249C12.8945 2.18597 10.7085 0 8.01202 0C5.31549 0 3.12953 2.18597 3.12953 4.88249V5.75275C1.53818 5.99659 0.319336 7.37145 0.319336 9.03098V14.6833C0.319336 16.5151 1.80425 18 3.63599 18H12.364C14.1958 18 15.6807 16.5151 15.6807 14.6833V9.03098C15.6807 7.3798 14.4741 6.01043 12.8945 5.75652ZM12.3637 7.11047H3.63565C2.57517 7.11047 1.71548 7.97016 1.71548 9.03064V14.683C1.71548 15.7435 2.57517 16.6032 3.63565 16.6032H12.3637C13.4242 16.6032 14.2839 15.7435 14.2839 14.683V9.03064C14.2839 7.97016 13.4242 7.11047 12.3637 7.11047ZM8.7102 13.078C9.24866 12.818 9.62013 12.2668 9.62013 11.6288C9.62013 10.7405 8.90004 10.0204 8.01177 10.0204C7.12349 10.0204 6.4034 10.7405 6.4034 11.6288C6.4034 12.2669 6.77505 12.8182 7.31371 13.0782V14.5457L7.31915 14.6333C7.36223 14.9776 7.65599 15.2439 8.01195 15.2439C8.39758 15.2439 8.7102 14.9313 8.7102 14.5457V13.078Z" fill="#393A3B"/>
                  </svg>
                  <span>Unlock Offers</span>
              </div>` : ''}
            ${offerItemHTML[0] ?? ieOffersItemHTML[0] ?? ""}
            ${!offerItemHTML[0] && ieOffersItemHTML[0] ? `</div>` : ""}
            <div class="cart-offer-callout-card-show-more-btn ${offers.length > 1 || ieOffers.length > 0 ? '' : 'hidden'}">
                ${offers.length > 1 || ieOffers.length > 0 ? '<span class="line"></span>' : ""}
                ${offers.length > 1 || (offers.length == 0 && ieOffers.length > 1) || ieOffers.length > 0 ? '<span class="btn">More Offers (' + (offers.length + ieOffers.filter(off => off.offerRules.amount.minOrderAmount > window.allOffers.cartData.cartTotal).length - 1) + ')<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-chevron-down" aria-hidden="true" focusable="false" role="presentation"><path d="m6 9 6 6 6-6"/></svg></span>' : ""}
            </div>
            <div class="cart-offer-callout-card-show-more-container hidden">
                <div class="cart-offer-callout-card-show-more-container-eligible">
                    ${offers.length > 1 ? offerItemHTML.slice(1).join("") : ""}
                </div>
                ${
                  ieOffersItemHTML.length > 1 && offers.length == 0
                    ? `
                <div class="cart-offer-callout-card-show-more-container-ineligible">
                    <div class="ineligible-title">
                        <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M4.52482 5.71433H11.4968L11.4976 4.88264C11.4976 2.95737 9.93688 1.39663 8.01161 1.39663L7.83763 1.4009C5.99319 1.49156 4.52561 3.01571 4.52561 4.88264L4.52482 5.71433ZM12.8945 5.75652V4.88249C12.8945 2.18597 10.7085 0 8.01202 0C5.31549 0 3.12953 2.18597 3.12953 4.88249V5.75275C1.53818 5.99659 0.319336 7.37145 0.319336 9.03098V14.6833C0.319336 16.5151 1.80425 18 3.63599 18H12.364C14.1958 18 15.6807 16.5151 15.6807 14.6833V9.03098C15.6807 7.3798 14.4741 6.01043 12.8945 5.75652ZM12.3637 7.11047H3.63565C2.57517 7.11047 1.71548 7.97016 1.71548 9.03064V14.683C1.71548 15.7435 2.57517 16.6032 3.63565 16.6032H12.3637C13.4242 16.6032 14.2839 15.7435 14.2839 14.683V9.03064C14.2839 7.97016 13.4242 7.11047 12.3637 7.11047ZM8.7102 13.078C9.24866 12.818 9.62013 12.2668 9.62013 11.6288C9.62013 10.7405 8.90004 10.0204 8.01177 10.0204C7.12349 10.0204 6.4034 10.7405 6.4034 11.6288C6.4034 12.2669 6.77505 12.8182 7.31371 13.0782V14.5457L7.31915 14.6333C7.36223 14.9776 7.65599 15.2439 8.01195 15.2439C8.39758 15.2439 8.7102 14.9313 8.7102 14.5457V13.078Z" fill="#393A3B"/>
                        </svg>
                        <span>Unlock Offers</span>
                    </div>
                    ${ieOffersItemHTML.slice(1).join("")}
                </div>
                `
                    : ""
                }
                ${
                  ieOffersItemHTML.length > 0 && offers.length != 0
                    ? `
                <div class="cart-offer-callout-card-show-more-container-ineligible">
                    <div class="ineligible-title">
                        <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M4.52482 5.71433H11.4968L11.4976 4.88264C11.4976 2.95737 9.93688 1.39663 8.01161 1.39663L7.83763 1.4009C5.99319 1.49156 4.52561 3.01571 4.52561 4.88264L4.52482 5.71433ZM12.8945 5.75652V4.88249C12.8945 2.18597 10.7085 0 8.01202 0C5.31549 0 3.12953 2.18597 3.12953 4.88249V5.75275C1.53818 5.99659 0.319336 7.37145 0.319336 9.03098V14.6833C0.319336 16.5151 1.80425 18 3.63599 18H12.364C14.1958 18 15.6807 16.5151 15.6807 14.6833V9.03098C15.6807 7.3798 14.4741 6.01043 12.8945 5.75652ZM12.3637 7.11047H3.63565C2.57517 7.11047 1.71548 7.97016 1.71548 9.03064V14.683C1.71548 15.7435 2.57517 16.6032 3.63565 16.6032H12.3637C13.4242 16.6032 14.2839 15.7435 14.2839 14.683V9.03064C14.2839 7.97016 13.4242 7.11047 12.3637 7.11047ZM8.7102 13.078C9.24866 12.818 9.62013 12.2668 9.62013 11.6288C9.62013 10.7405 8.90004 10.0204 8.01177 10.0204C7.12349 10.0204 6.4034 10.7405 6.4034 11.6288C6.4034 12.2669 6.77505 12.8182 7.31371 13.0782V14.5457L7.31915 14.6333C7.36223 14.9776 7.65599 15.2439 8.01195 15.2439C8.39758 15.2439 8.7102 14.9313 8.7102 14.5457V13.078Z" fill="#393A3B"/>
                        </svg>
                        <span>Unlock Offers</span>
                    </div>
                    ${ieOffersItemHTML.join("")}
                </div>
                `
                    : ""
                }
            </div>
        </extra-offers-container>
    `;
};

/**
 * Custom element for handling expandable offers container
 * Manages show/hide state and animation of additional offers
 */
class ExtraOffersContainer extends HTMLElement {
  constructor() {
    super();
    this.querySelector(
      ".cart-offer-callout-card-show-more-btn",
    ).addEventListener("click", () => {
      if (
        this.querySelector(
          ".cart-offer-callout-card-show-more-container",
        ).classList.contains("hidden")
      ) {
        this.showMoreOffers();
      } else {
        this.hideMoreOffers();
      }
    });

    this.showMoreOffers = () => {
      this.querySelector(
        ".cart-offer-callout-card-show-more-container",
      ).classList.remove("hidden");
      this.querySelector(
        ".cart-offer-callout-card-show-more-btn svg",
      ).style.transform = "rotate(180deg)";
    };

    this.hideMoreOffers = () => {
      this.querySelector(
        ".cart-offer-callout-card-show-more-container",
      ).classList.add("hidden");
      this.querySelector(
        ".cart-offer-callout-card-show-more-btn svg",
      ).style.transform = "rotate(0deg)";
    };
  }
}

/**
 *
 * Processes response data from Breeze API
 * - Filteres actions based on payload.action
 * - Calls respective functions to handle each action
 */

window.breeze.processResponse = async function (offersResponse) {
  const response = JSON.parse(offersResponse);
  const { requestId, payload } = response;
  const { action, event } = payload;
  if (action === "listOffers") {
    window.breeze.offersCallback(response);
  } else if (action === "startCheckout") {
    if (event === "Purchase") {
      let { data } = payload;
      const customEvent = new CustomEvent("CHECKOUT_PROCESS_INTERNAL", {
        detail: { requestId, event, data },
      });
      document.dispatchEvent(customEvent);
    } else if (["OfferApplied", "OfferRemoved", "PayNow"].includes(event)){
      const data = response
      const customEvent = new CustomEvent("CHECKOUT_PROCESS_INTERNAL", {
        detail: { requestId, event, data },
      });
      console.log("Custom event dispatched:", customEvent);
      document.dispatchEvent(customEvent);
    } else {
      const customEvent = new CustomEvent("CHECKOUT_PROCESS_INTERNAL", {
        detail: { requestId, event },
      });
      document.dispatchEvent(customEvent);
    }
  }
};

/**
 * Processes offer data from Breeze API response
 * - Filters for eligible offers
 * - Stores offer data globally
 * - Renders offers in cart drawer or PDP based on request ID
 * - Initializes custom elements
 */
window.breeze.offersCallback = function (offers) {

  // console.log("Request ID - ", offers.requestId);
  const offersData = (offers.payload.offers || []).filter(
    (offer) => offer?.status === "ELIGIBLE",
  );
  const inEligibleOffers = (offers.payload.offers || []).filter(
    (offer) => offer?.status !== "ELIGIBLE",
  );

  // Check request ID to determine context
  const requestId = offers.requestId || "";
  if (requestId.includes("cart")) {
    // Cart offers
    try {
      window.allOffers.cartOffers = offersData;
      window.allOffers.inEligibleOffers.cart = inEligibleOffers;
      const offerContainer = document.querySelector(".cart-drawer__offers");
      offerContainer.innerHTML = window.BlazeSDK.generateCartOfferHTML(
        window.allOffers.cartOffers,
        window.allOffers.inEligibleOffers.cart,
      );
      offerContainer
        .querySelectorAll(
          ".cart-offer-callout-card-show-more-container-ineligible .unlock-offer",
        )
        .forEach((unlockOffer) => {
          unlockOffer.classList.remove("hidden");
        });
    } catch (error) {
      console.error("Error rendering cart offers:", error);
    }
  } else if (requestId.includes("pdp")) {
    // PDP offers
    console.log("PDP Offers Payload");
    window.allOffers.pdpOffers = offersData;
    window.allOffers.inEligibleOffers.pdpOffers = inEligibleOffers;
    const offerParentContainer = document.querySelector(
      ".product-discount-container",
    );
    const offerContainer = document.querySelector(
      ".product-discount-container-inner",
    );
    const additionalOffersContainerParent = document.querySelector(
      ".product-discount-additional-container",
    );
    const additionalOffersContainer = document.querySelector(
      ".product-discount-additional-container .product-discount-additional",
    );
    const offerModalContainer = document.querySelector(".offer-modal-body");
    try {
      if (offersData.length == 0 && inEligibleOffers.length > 0) {
        additionalOffersContainerParent.classList.add("grayed-out");
        additionalOffersContainer.classList.add("aligncenter");
        additionalOffersContainer.innerHTML = `
        <svg width="15" height="15" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M4.52482 5.71433H11.4968L11.4976 4.88264C11.4976 2.95737 9.93688 1.39663 8.01161 1.39663L7.83763 1.4009C5.99319 1.49156 4.52561 3.01571 4.52561 4.88264L4.52482 5.71433ZM12.8945 5.75652V4.88249C12.8945 2.18597 10.7085 0 8.01202 0C5.31549 0 3.12953 2.18597 3.12953 4.88249V5.75275C1.53818 5.99659 0.319336 7.37145 0.319336 9.03098V14.6833C0.319336 16.5151 1.80425 18 3.63599 18H12.364C14.1958 18 15.6807 16.5151 15.6807 14.6833V9.03098C15.6807 7.3798 14.4741 6.01043 12.8945 5.75652ZM12.3637 7.11047H3.63565C2.57517 7.11047 1.71548 7.97016 1.71548 9.03064V14.683C1.71548 15.7435 2.57517 16.6032 3.63565 16.6032H12.3637C13.4242 16.6032 14.2839 15.7435 14.2839 14.683V9.03064C14.2839 7.97016 13.4242 7.11047 12.3637 7.11047ZM8.7102 13.078C9.24866 12.818 9.62013 12.2668 9.62013 11.6288C9.62013 10.7405 8.90004 10.0204 8.01177 10.0204C7.12349 10.0204 6.4034 10.7405 6.4034 11.6288C6.4034 12.2669 6.77505 12.8182 7.31371 13.0782V14.5457L7.31915 14.6333C7.36223 14.9776 7.65599 15.2439 8.01195 15.2439C8.39758 15.2439 8.7102 14.9313 8.7102 14.5457V13.078Z" fill="#393A3B"/>
        </svg>
        Unlock Offers`;
      }
      window.allOffers.pdpOffers
        .sort((a, b) => b.orderBreakup.offerAmount - a.orderBreakup.offerAmount)
        .forEach((offer, index) => {
          // Check if offer with this ID already exists
          const existingOffer = document.querySelector(
            `[data-offer-id="${offer.offerId}"]`,
          );
          if (!existingOffer) {
            const offerElement = document.createElement("div");
            offerElement.classList.add("offer-item");
            offerElement.setAttribute("data-offer-id", offer.offerId);
            offerElement.innerHTML = window.BlazeSDK.generatePDPOfferHTML(
              offer,
              index + 1,
              true,
            );

            if (index === 0) {
              offerContainer.appendChild(offerElement);
            }

            const modalOfferElement = document.createElement("div");
            modalOfferElement.classList.add("modal-offer-item");
            modalOfferElement.setAttribute("data-offer-id", offer.offerId);
            modalOfferElement.innerHTML = window.BlazeSDK.generatePDPOfferHTML(
              offer,
              index + 1,
            );
            offerModalContainer.append(modalOfferElement); // Skip if offer already exists
          }
        });
    } finally {
      if(offersData.length > 0 || inEligibleOffers.length > 0){
        offerParentContainer.classList.remove("hidden");
      }
      const footerElement = offerParentContainer.querySelector(
        ".cart-offer-callout-card-body-footer",
      );
      if (footerElement) {
        footerElement.style.display = "none";
      }
    }
  }
};

// Component Registration

/**
 * Registers custom element for individual offer items
 * Handles expanding/collapsing terms and conditions
 */
document.addEventListener("DOMContentLoaded", async () => {
  class DiscountCodeItem extends HTMLElement {
    constructor() {
      super();
      let _this = this;
      this.querySelector(
        ".cart-offer-callout-card-know-more-btn",
      ).addEventListener("click", (event) => {
        _this.showTnC();
      });
      this.querySelector(
        ".cart-offer-callout-card-show-less-btn",
      ).addEventListener("click", (event) => {
        _this.hideTnC();
      });
      const applyBtn = this.querySelector(
        ".cart-offer-apply-container.apply-btn",
      );
      if (applyBtn) {
        applyBtn.addEventListener("click", (event) => {
          let offer = {
            id: this.querySelector(".offer-code")?.getAttribute(
              "data-offer-id",
            ),
            code: this.querySelector(".offer-code")?.getAttribute(
              "data-offer-code",
            ),
            cartTotal:
              this.querySelector(".offer-code")?.getAttribute(
                "data-cart-total",
              ),
          };
          this.applyOffer(offer, this.querySelector(".apply-btn-text"));
        });
      } else {
        console.warn("Apply button not found in DiscountCodeItem");
      }

      // Check for applied offer and update button text
      const appliedOffer = JSON.parse(localStorage.getItem("appliedOffer"));
      if (
        appliedOffer &&
        appliedOffer.code ===
          this.querySelector(".offer-code").getAttribute("data-offer-code")
      ) {
        this.querySelector(".apply-btn-text").innerHTML = "REMOVE";
      }
      let retryCount = 0;
      const maxRetries = 5;

      const waitForCartOffers = () => {
        if (
          window.allOffers.cartOffers &&
          window.allOffers.cartOffers.length > 0
        ) {
          if (appliedOffer) {
            const offer = window.allOffers.cartOffers.find(
              (o) => o.offerCode === appliedOffer.code,
            );
            if (offer && offer.status === "ELIGIBLE") {
              localStorage.setItem(
                "appliedOffer",
                JSON.stringify({
                  id: offer.offerId,
                  code: offer.offerCode,
                  cartTotal: offer.orderBreakup.finalOrderAmount,
                }),
              );
              document.querySelector(
                ".cart-drawer__footer .subtotal .theme-money",
              ).innerHTML = this.formatIndianRupees(
                Math.trunc(offer.orderBreakup.finalOrderAmount),
              );
            } else {
              localStorage.removeItem("appliedOffer");
              document.dispatchEvent(
                new CustomEvent("dispatch:cart-drawer:refresh"),
              );
            }
          }
        } else if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(waitForCartOffers, 100); // Retry after 100ms
        }
      };

      waitForCartOffers();
    }

    formatIndianRupees(amount) {
      let formatter = new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
      });

      return formatter.format(amount).replace("₹", "Rs.");
    }

    applyOffer(offer, el) {
      let appliedOffer = JSON.parse(localStorage.getItem("appliedOffer"));
      if (appliedOffer && appliedOffer.code === offer.code) {
        localStorage.removeItem("appliedOffer");
        document
          .querySelectorAll(".apply-btn-text")
          .forEach((ele) => (ele.innerHTML = "APPLY"));
        el.innerHTML = "APPLY";
        document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
      } else {
        localStorage.setItem("appliedOffer", JSON.stringify(offer));
        document
          .querySelectorAll(".apply-btn-text")
          .forEach((ele) => (ele.innerHTML = "APPLY"));
        el.innerHTML = "REMOVE";
        document.querySelector(
          ".cart-drawer__footer .subtotal .theme-money",
        ).innerHTML = this.formatIndianRupees(Math.trunc(offer.cartTotal));
        this.showAppliedCoupon();
      }
    }

    copyOfferCode() {
      const offerCode = this.querySelector(
        ".cart-offer-callout-card-body-header span",
      ).innerText;
      navigator.clipboard.writeText(offerCode);
      window.csToast.showToast(
        "success",
        `Code Copied <span class="toast-bold">${offerCode}</span>! <br>Proceed to checkout to Apply Offers`,
      );
    }

    showAppliedCoupon() {
      const offerCode = this.querySelector(
        ".cart-offer-callout-card-body-header .offer-code",
      ).innerText;
      const discountAmount = this.querySelector(
        ".cart-offer-apply-container p span",
      ).getAttribute("data-discount-amount");
      const cartOfferApplied = document.querySelector("cart-offer-applied");
      if (cartOfferApplied) {
        cartOfferApplied.show(`₹${discountAmount} saved`, offerCode);
      }
    }

    showTnC() {
      this.querySelector(
        ".cart-offer-callout-card-tc-container",
      ).classList.remove("hidden");
      this.querySelector(
        ".cart-offer-callout-card-show-less-btn",
      ).classList.remove("hidden");
      this.querySelector(
        ".cart-offer-callout-card-know-more-btn",
      ).classList.add("hidden");
    }

    hideTnC() {
      this.querySelector(".cart-offer-callout-card-tc-container").classList.add(
        "hidden",
      );
      this.querySelector(
        ".cart-offer-callout-card-show-less-btn",
      ).classList.add("hidden");
      this.querySelector(
        ".cart-offer-callout-card-know-more-btn",
      ).classList.remove("hidden");
    }
  }

  customElements.define("discount-code-item", DiscountCodeItem);
});

// PDP Offers Rendering
const fetchAndRenderPDPOffers = async (reason = "") => {
  // Clear existing timer
  if (pdpOffersDebounceTimer) {
    clearTimeout(pdpOffersDebounceTimer);
  }
  
  // Set new timer
  pdpOffersDebounceTimer = setTimeout(async () => {
    // Prevent multiple simultaneous requests
    if (pdpOffersRequestInProgress) {
      console.log("PDP Offers request already in progress, skipping...");
      return;
    }
    
    pdpOffersRequestInProgress = true;
    
    try {
      const cartItems = await window.cultstore.productData;
      const customerId = getCustomerIdFromLocalStorage();
      const transformedCartItems = [cartItems].map((item) => ({
        id: item.selectedVariant.id.toString(),
        type: item.productType.replace(/\s+/g, "_"),
        price: (item.selectedVariant.price / 100).toString(),
        quantity: 1,
      }));
      
      const payload = {
        keyId: "10291",
        action: "listOffers",
        customerData: {
          id: customerId,
        },
        cart: JSON.stringify({
          items: transformedCartItems,
        }),
      };
      
      const shopifyYCookie = getCookie("_shopify_y");
      
      const response = await fetch(
        "https://tesseract.cultstore.com/juspay/gen-offer-signature",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Device-ID": shopifyYCookie ?? "",
          },
          body: JSON.stringify(payload),
        },
      );
      
      const jsonResponse = await response.json();
      const signature = jsonResponse.data.signature;
      const requestId = window.breeze.generateShortUniqueId("pdp");
      const processPayload = {
        requestId: requestId,
        service: "in.breeze.onecco",
        payload: {
          ...payload,
          signature: signature,
        },
      };
      
      if (!window.BlazeSDK || typeof window.BlazeSDK.process !== "function") {
        console.error("BlazeSDK is not initialized or process is not a function");
        return;
      }
      
      window.BlazeSDK.process(processPayload);
      // console.log("Fetching PDP Offers due to:", reason);
    } catch (error) {
      console.error("Error fetching PDP offers:", error);
    } finally {
      pdpOffersRequestInProgress = false;
    }
  }, 300); // 300ms debounce delay
};

/**
 * Generates HTML for individual offer card
 * Includes:
 * - Best offer badge for first offer
 * - Offer code and description
 * - Expandable terms and conditions
 * - Show/hide buttons for T&C
 */
window.BlazeSDK.generatePDPOfferHTML = function (offer, index, isPdp = false) {
  let offerItemHTML = window.breeze.generateCartOfferItemHTML(
    offer,
    index,
    isPdp,
  );
  return `
        <div class="modal-offer-item">
            ${offerItemHTML}
        </div>
    `;
};

/**
 * Generates HTML for individual offer card
 * Includes:
 * - Offer code and description
 * - Expandable terms and conditions
 * - Show/hide buttons for T&C
 */
// const generatePDPOfferItemHTML = (offer) => {
//     return `
//         <discount-code>
//           <div class="product-discount-wrapper">
//             <div class="product-discount-details">
//               <p><span>${offer.offerCode}</span> ${offer.offerDescription.description}</p>
//             </div>
//             <div class="product-discount-title" data-code="${offer.offerCode}">
//               ${offer.offerCode}
//               <span class="copy-tooltip">Code copied</span>
//             </div>
//           </div>
//           <div class="product-discount-tc">
//             <button class="tc-know-more-btn">Know more</button>
//             <div class="tc-continer hidden">
//               <div class="tc-header">Terms and Conditions</div>
//               <ul class="tc-pointer">
//                 ${generatePDPOfferItemTnCHTML(offer.offerDescription.tnc || 'N/A')}
//               </ul>
//             </div>
//             <button class="tc-show-less-btn hidden">Show less</button>
//           </div>
//         </discount-code>
//     `;
// }

/**
 * Generates HTML for T&C items
 * - Splits T&C string by '-'
 * - Filters out empty items
 * - Maps each item to a list item
 * - Joins all items with a line break
 */

const generatePDPOfferItemTnCHTML = (offerTnC) => {
  const tncItems = offerTnC
    .replace(/<div class=['"]product-categories['"][^>]*>[\s\S]*?<\/div>/, '')
    .split("-")
    .filter((item) => item.trim() != "" && item.trim() != " ")
    .map((item) => `<li>${item.replace(/\n/g, "").trim()}</li>`)
    .join("");
  return `${tncItems}`;
};

/**
 * Main initialization
 * - Fetches initial offers
 * - Configures BlazeSDK
 * - Sets up cart change listener to refresh offers
 */
document.addEventListener("DOMContentLoaded", async () => {
  customElements.define("extra-offers-container", ExtraOffersContainer);

  let blazeSDKInitialized = false;

  const waitForBlazeSDK = (() => {
    const startTime = Date.now();
    let errorLogged = false;

    return function check() {
      if (window.BlazeSDK && typeof window.BlazeSDK.initiate === "function" && !blazeSDKInitialized) {
        blazeSDKInitialized = true;
        
        window.BlazeSDK.initiate(
          {
            merchantId: "curefit",
            shopUrl: "https://cult-demo.myshopify.com/",
            environment: "production",
            platform: "Shopify",
          },
          window.breeze.processResponse,
        );
        
        if (window.location.pathname.includes("/products/")) {
          fetchAndRenderPDPOffers("PDP Visited");
        }
        fetchAndRenderOffersInCart();
      } else if (!blazeSDKInitialized) {
        const elapsed = Date.now() - startTime;
        if (elapsed > 2000 && !errorLogged) {
          errorLogged = true;
          console.error(
            "PDP Offers Error: BlazeSDK did not initialize within 2 seconds.",
          );
        }
        setTimeout(check, 100);
      }
    };
  })();

  waitForBlazeSDK();

  // Refresh offers when cart changes - with debouncing
  window.addEventListener("cart_changed", (e) => {
    window.allOffers.cartData.cartTotal = e.detail.cartTotal;
    fetchAndRenderOffersInCart(e.detail.cart);
    if (window.location.pathname.includes("/products/")) {
      fetchAndRenderPDPOffers("Cart changed");
    }
  });
});

class CartOfferApplied extends HTMLElement {
  constructor() {
    super();
    // this.querySelector('.cart-offer-applied__close').addEventListener('click', () => {
    //   this.close();
    // });
  }

  show(title, coupon, duration = 3000) {
    this.querySelector(".cart-offer-applied__title").innerText = title;
    this.querySelector(".cart-offer-applied__description span").innerText =
      coupon;
    this.style.display = "flex";
    setTimeout(() => {
      this.close();
    }, duration);
  }

  close() {
    this.style.display = "none";
  }
}

customElements.define("cart-offer-applied", CartOfferApplied);
